import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { SyncEngine, type EngineState, type MutationResult } from '@edv-next/sync-engine';
import { createIndexedDbStorage } from '@edv-next/storage';
import { api } from './api.js';

interface SyncCtx {
  state: EngineState;
  engine: SyncEngine;
  clearSticky(): void;
}
const Ctx = createContext<SyncCtx | null>(null);

export function SyncProvider({
  userId,
  orgId,
  children,
}: {
  userId: string;
  orgId: string;
  children: React.ReactNode;
}) {
  const [state, setState] = useState<EngineState>('idle');
  const engine = useMemo(() => {
    const storage = createIndexedDbStorage({
      dbName: `edv-next.${userId}.${orgId}`,
      schemaVersion: 1,
    });
    const transport = {
      async push(muts: unknown[]): Promise<MutationResult[]> {
        const r = await api.syncPush(muts as never);
        return r.results.map((item): MutationResult => {
          const result: MutationResult = {
            result: item.result === 'already_applied' ? 'saved' : item.result,
          };
          if (item.canonical_entity !== undefined) result.canonical_entity = item.canonical_entity;
          if (item.new_version !== undefined) result.new_version = item.new_version;
          if (item.site_id !== undefined) result.site_id = item.site_id;
          if (item.module_key !== undefined) result.module_key = item.module_key;
          if (item.error !== undefined) result.error = item.error;
          if (item.site_change_seq !== undefined) {
            const sequence = Number(item.site_change_seq);
            if (!Number.isSafeInteger(sequence)) {
              throw new Error('site_change_seq exceeds the web sync engine safe-integer range');
            }
            result.site_change_seq = sequence;
          }
          return result;
        });
      },
      async pull(site_id: string, after: number, limit: number) {
        const r = await api.syncPull({ site_id, after: String(after), limit });
        return {
          site_id: r.site_id,
          changes: r.changes,
          next_after: Number(r.next_after),
          has_more: r.has_more,
        };
      },
      async advanceCheckpoint(site_id: string, last_seq: number) {
        await api.syncCheckpointAdvance({ site_id, last_seq: String(last_seq) });
      },
    };
    return new SyncEngine({ storage, transport });
  }, [userId, orgId]);
  useEffect(() => engine.subscribe(setState), [engine]);
  return (
    <Ctx.Provider value={{ state, engine, clearSticky: () => engine.clearSticky() }}>
      {children}
    </Ctx.Provider>
  );
}
export function useSync(): SyncCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('SyncProvider missing');
  return v;
}
